import {
  DestroyRef,
  Injectable,
  Signal,
  WritableSignal,
  computed,
  effect,
  inject,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, Subject, catchError, map, of } from 'rxjs';
import {
  GRID_CONFIG_SOURCE,
  GRID_FEATURES,
  GRID_ROW_OBSERVER,
  type GridBucketRowsPage,
  type GridBucketScope,
  type GridColumnRemapping,
  type GridConfig,
  type GridGroupSummaryItem,
  type GridRow,
  type GridRowsPage,
  type GridSelectionDescriptor,
  type GridSelectionQuery,
  type GridSortField
} from '../contracts/public-api';
import type { GridFeatures } from '../contracts/grid-features.interface';
import {
  DEFAULT_GRID_ORCHESTRATION_OPTIONS,
  GRID_ORCHESTRATION_OPTIONS,
  type GridOrchestrationOptions
} from '../contracts/grid-orchestration-options.interface';
import { GRID_STATE_PERSISTENCE } from '../in-memory-grid-state-persistence';
import type {
  GridBackendAttributeFilter,
  GridBackendAttributeFilterType,
  ColumnFilter,
  ColumnFilterMap
} from '../d-data-grid/column-filter.types';
import {
  buildWhereFromFilters,
  deserializeFilters,
  isFilterActive,
  serializeFilters
} from '../d-data-grid/column-filter.utils';
import {
  DataGridColumn,
  DataGridState,
  DataGridGroupBucketViewState,
  SortDirection
} from '../d-data-grid/data-grid.types';
import type { GridScopeFilterMode } from '../contracts/data-grid-state.interface';
import { asGridSourceRow, type GridSourceRow } from './grid-source-row.interface';
import { GridOrchestrationDataAccess } from './grid-orchestration-data-access';
import {
  buildCurrentAttributeFilters,
  buildNullOperatorEntry,
  mergeAttributeFilters as mergeAttributeFilterEntries,
  resolveColumnType as resolveColumnDataType,
  splitWhereByCanonicalAlias
} from './grid-orchestration-attribute-filter';
import {
  fetchBucketChunkFirstPages,
  fetchBucketsFirstPages,
  fetchPerGroupFirstPages as runFetchPerGroupFirstPages,
  mergeFirstPageBatchResults as runMergeFirstPageBatchResults,
  type BucketLoadingHost
} from './grid-orchestration-bucket-loading';
import {
  buildWhereFromPath as buildBucketWhereFromPath,
  canonicalizeDiscriminatorBucketPath,
  displayDiscriminatorBucketPath,
  isAliasedDiscriminatorPaginationKey
} from './grid-orchestration-bucket-path';
import {
  discoverSubGroups as runDiscoverSubGroups,
  discoverSubGroupsViaTree as runDiscoverSubGroupsViaTree,
  type GroupDiscoveryHost
} from './grid-orchestration-group-discovery';
import {
  canFetchMorePrimaryGroupSummaries,
  hasUnrevealedPrimaryGroupHeaders,
  fetchAllPrimaryGroupSummaries as runFetchAllPrimaryGroupSummaries,
  loadMoreGroupHeaders as runLoadMoreGroupHeaders,
  seedPrimaryGroupSkeleton as runSeedPrimaryGroupSkeleton,
  seedPrimaryGroupSkeletonWithHybridEager as runSeedPrimaryGroupSkeletonWithHybridEager,
  type GroupSummaryHost
} from './grid-orchestration-group-summary';
import {
  buildBucketLoadingHost as createBucketLoadingHostAdapter,
  buildGroupDiscoveryHost as createGroupDiscoveryHostAdapter,
  buildPaginationHost as createPaginationHostAdapter,
  type GridOrchestrationHostContext
} from './grid-orchestration-hosts';
import {
  isEmptyElementsResponse as checkIsEmptyElementsResponse,
  isNullBucketPath as checkIsNullBucketPath,
  resolveNullAliasAttributeFilters,
  fetchGridPage as runFetchGridPage,
  fetchNullBucketViaClientFilter as runFetchNullBucketViaClientFilter,
  type NullBucketHost
} from './grid-orchestration-null-bucket';
import {
  loadNextFlatGridPage,
  applyBucketFetchResult as runApplyBucketFetchResult,
  flushBatchLoadQueue as runFlushBatchLoadQueue,
  loadBucket as runLoadBucket,
  mergeGroupPaginationIntoElements as runMergeGroupPaginationIntoElements,
  scheduleBucketLoad as runScheduleBucketLoad,
  updateGroupPaginationState as runUpdateGroupPaginationState,
  type GridPaginationHost
} from './grid-orchestration-pagination';
import { sumPrimaryBucketTotals } from './grid-orchestration-pagination-state';
import {
  subscribeElementsQueryPipeline,
  type ElementsQueryPipelineHost
} from './grid-orchestration-query-pipeline';
import { GROUP_PATH_DELIMITER } from './grid-orchestration.constants';
import { GridOrchestrationLogLevel, logGridOrchestration } from './grid-orchestration.dev-log';
import type { GroupPaginationState, GroupingCapWarning } from './grid-orchestration.types';
import {
  BATCH_FIRST_PER_BUCKET,
  BATCH_FLUSH_MS,
  GROUP_HEADER_PAGE_SIZE,
  MAX_REQUEST_CONCURRENCY,
  forkJoinWithConcurrency,
  mergeGroupingCapWarnings
} from './grid-orchestration.utils';

/** Re-export so existing importers of `GridColumnRemapping` from this service keep compiling. */
export type { GridColumnRemapping };

/** Re-export pagination state type for tests and consumers. */
export type { GroupPaginationState };

/** Shared empty lookup reused to avoid per-row Map allocations when no remappings exist. */
const EMPTY_REMAPPING_LOOKUP: ReadonlyMap<string, string> = new Map<string, string>();

/** Shared empty set reused when no column remappings are configured. */
const EMPTY_REMAPPED_FIELDS: ReadonlySet<string> = new Set<string>();

/** Shared frozen empty attributes object reused for rows whose element has no
 * attributes. Avoids allocating a fresh `{}` per row in the `gridData` hot loop.
 */
const EMPTY_ATTRS: Readonly<Record<string, unknown>> = Object.freeze({});

/** Candidate JSONB attribute keys for legacy PMS from/at measure storage. */
const FROM_MEASURE_ATTRIBUTE_KEYS: ReadonlyArray<string> = [
  'fromMeasure',
  'From',
  'from',
  'At',
  'at',
  'atMeasure'
];
const TO_MEASURE_ATTRIBUTE_KEYS: ReadonlyArray<string> = ['toMeasure', 'To', 'to'];

/**
 * Reads the first numeric measure value found under any of the given attribute keys.
 */
function readAttributeMeasure(
  attributes: Readonly<Record<string, unknown>>,
  keys: ReadonlyArray<string>
): number | undefined {
  for (const key of keys) {
    const value: unknown = attributes[key];
    if (typeof value === 'number' && !Number.isNaN(value)) {
      return value;
    }
    if (typeof value === 'string' && value.trim() !== '') {
      const parsed: number = Number(value);
      if (!Number.isNaN(parsed)) {
        return parsed;
      }
    }
  }
  return undefined;
}

/**
 * Parses linear measures from display names such as `7949 @ 0.000 - 0.790`
 * or point-asset names such as `72 @ 1.234`.
 */
function parseMeasuresFromDisplayName(displayName: string | undefined): {
  fromMeasure: number | undefined;
  toMeasure: number | undefined;
} {
  if (!displayName) {
    return { fromMeasure: undefined, toMeasure: undefined };
  }

  const rangeMatch: RegExpMatchArray | null = displayName.match(/@\s*([\d.]+)\s*-\s*([\d.]+)\s*$/);
  if (rangeMatch) {
    const fromMeasure: number = Number(rangeMatch[1]);
    const toMeasure: number = Number(rangeMatch[2]);
    if (!Number.isNaN(fromMeasure) && !Number.isNaN(toMeasure)) {
      return { fromMeasure, toMeasure };
    }
  }

  const atMatch: RegExpMatchArray | null = displayName.match(/@\s*([\d.]+)\s*$/);
  if (atMatch) {
    const fromMeasure: number = Number(atMatch[1]);
    if (!Number.isNaN(fromMeasure)) {
      return { fromMeasure, toMeasure: undefined };
    }
  }

  return { fromMeasure: undefined, toMeasure: undefined };
}

/**
 * Resolves linear measure values from an element response.
 *
 * Resolution order:
 * 1. Top-level `fromMeasure` / `toMeasure` on `Element`
 * 2. Linked `lrmPosition` navigation field
 * 3. Legacy JSONB attribute keys (`From` / `To` / `At`, etc.)
 * 4. `displayName` parse fallback (`route @ from - to`, or `route @ at` for point assets)
 */
function resolveElementMeasures(element: GridSourceRow): {
  fromMeasure: number | undefined;
  toMeasure: number | undefined;
} {
  let fromMeasure: number | undefined = element.fromMeasure ?? element.lrmPosition?.fromMeasure ?? undefined;
  let toMeasure: number | undefined = element.toMeasure ?? element.lrmPosition?.toMeasure ?? undefined;

  const attributes: Readonly<Record<string, unknown>> | undefined = element.attributes;
  if (attributes) {
    if (fromMeasure === undefined) {
      fromMeasure = readAttributeMeasure(attributes, FROM_MEASURE_ATTRIBUTE_KEYS);
    }
    if (toMeasure === undefined) {
      toMeasure = readAttributeMeasure(attributes, TO_MEASURE_ATTRIBUTE_KEYS);
    }
  }

  if (fromMeasure === undefined || toMeasure === undefined) {
    const parsed: { fromMeasure: number | undefined; toMeasure: number | undefined } =
      parseMeasuresFromDisplayName(element.displayName);
    if (fromMeasure === undefined) {
      fromMeasure = parsed.fromMeasure;
    }
    if (toMeasure === undefined) {
      toMeasure = parsed.toMeasure;
    }
  }

  return { fromMeasure, toMeasure };
}

/**
 * Applies top-level element scalar fields onto a grid row after JSONB attributes
 * are merged, so GraphQL-resolved system fields are never overwritten by
 * attribute keys with the same name.
 */
function applyElementTopLevelFields(row: Record<string, unknown>, el: GridSourceRow): void {
  const measures: { fromMeasure: number | undefined; toMeasure: number | undefined } =
    resolveElementMeasures(el);

  row['key'] = el.key;
  row['assetId'] = el.id;
  row['elementType'] = el.elementType;
  row['displayName'] = el.displayName;
  row['publicKey'] = el.publicKey;
  row['objectStatus'] = el.objectStatus;
  row['recordStatus'] = el.recordStatus;
  row['validOn'] = el.validOn;
  row['validTo'] = el.validTo;
  if (measures.fromMeasure !== undefined) {
    row['fromMeasure'] = measures.fromMeasure;
  }
  if (measures.toMeasure !== undefined) {
    row['toMeasure'] = measures.toMeasure;
  }
  row['recordDate'] = el.recordDate;
  row['editor'] = el.editor;
  row['editReason'] = el.editReason;
  row['editComment'] = el.editComment;
  row['affectedRecordDate'] = el.affectedRecordDate;
  row['lrmPositionId'] = el.lrmPositionId;
}

/**
 * Service that orchestrates grid data fetching, pagination cursors,
 * grouping logic, and sort order management.
 *
 * Scoped to the component tree (not root) — holds per-layout state.
 * Designed for future portability to `shared-ui`.
 */
@Injectable()
export class GridOrchestrationService {
  /** Host-provided data access for grid queries. */
  private readonly elementsProvider: GridOrchestrationDataAccess = inject(GridOrchestrationDataAccess);

  /** Host-provided grid state persistence. */
  private readonly persistence = inject(GRID_STATE_PERSISTENCE);

  /** Host-provided grid configuration stream (optional for standalone grids). */
  private readonly configSource = inject(GRID_CONFIG_SOURCE, { optional: true });

  /** Feature preset — drives default scope filter mode. */
  private readonly gridFeatures: GridFeatures = inject(GRID_FEATURES);

  /** Optional host hook when rows are loaded (e.g. map coordinate registration). */
  private readonly rowObserver = inject(GRID_ROW_OBSERVER, { optional: true });

  /** Tunable orchestration options including the discriminator field name. */
  private readonly orchestrationOptions: GridOrchestrationOptions =
    inject(GRID_ORCHESTRATION_OPTIONS, {
      optional: true
    }) ?? DEFAULT_GRID_ORCHESTRATION_OPTIONS;

  /** DestroyRef used with takeUntilDestroyed for reactive pipeline cleanup. */
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  /** Subject that drives the debounced elements query pipeline. */
  private readonly elementsQuery$: Subject<Record<string, unknown>> = new Subject<Record<string, unknown>>();

  // ── Writable signals ────────────────────────────────────────────────

  /** Loaded elements data. */
  private readonly _elements: WritableSignal<GridRowsPage | null> = signal<GridRowsPage | null>(null);

  /** Error message if elements query fails. */
  private readonly _elementsError: WritableSignal<string | null> = signal<string | null>(null);

  /** Whether the initial grid data fetch is in progress. */
  private readonly _gridLoading: WritableSignal<boolean> = signal<boolean>(true);

  /** Whether the group-summary / discovery phase is in progress. */
  private readonly _gridSummaryLoading: WritableSignal<boolean> = signal<boolean>(false);

  /**
   * Set when grouped loading hits orchestration caps (primary eager-load or
   * sub-group discovery). Cleared on each new query; surfaced in the grid UI.
   */
  private readonly _groupingCapWarnings: WritableSignal<Array<GroupingCapWarning>> = signal<
    Array<GroupingCapWarning>
  >(new Array<GroupingCapWarning>());

  /** Cursor for the next page of grid elements. Null when all pages are loaded. */
  private readonly _gridEndCursor: WritableSignal<string | null> = signal<string | null>(null);

  /** Whether more pages are available from the server. */
  private readonly _gridHasMore: WritableSignal<boolean> = signal<boolean>(false);

  /** Whether a "load more" page request is currently in flight. */
  private readonly _gridLoadingMore: WritableSignal<boolean> = signal<boolean>(false);

  /**
   * Current server-side sort for the grid elements query.
   * Null means no sort applied (server default order).
   */
  private readonly _gridSort: WritableSignal<Array<GridSortField> | null> =
    signal<Array<GridSortField> | null>(null);

  /**
   * Current grid group-by fields, synced from grid state changes.
   * Initialized from persisted store state so the first query uses the correct mode.
   * Updated dynamically via `onGridGroupingChange()`.
   */
  private readonly _gridGroupByFields: WritableSignal<Array<string>> = signal<Array<string>>(
    this.persistence.state().groupByFields
  );

  /**
   * The outermost group field used for per-group pagination.
   * Null when ungrouped (flat mode). Set by the pipeline switchMap.
   */
  private readonly _primaryGroupField: WritableSignal<string | null> = signal<string | null>(null);

  /** Per-group pagination state. Null when not in per-group mode. */
  private readonly _groupPagination: WritableSignal<Map<string, GroupPaginationState> | null> = signal<Map<
    string,
    GroupPaginationState
  > | null>(null);

  /**
   * Server-provided totalCount per sub-group, for accurate nested group header counts.
   * Keyed as `"primaryGroupKey:::subGroupValue"` → count.
   * Populated by `discoverSubGroups` and reset on each new pipeline execution.
   */
  private readonly _subGroupCounts: WritableSignal<Record<string, number>> = signal<Record<string, number>>(
    {}
  );

  /**
   * Group fields detected as high-cardinality (distinct values exceed
   * {@link COLLAPSE_CARDINALITY_THRESHOLD}). The grid collapses these levels by
   * default and loads them lazily on expand; low-cardinality levels stay
   * expanded and eagerly loaded. Recomputed per query from the summary results.
   */
  private readonly _highCardinalityFields: WritableSignal<Set<string>> = signal<Set<string>>(
    new Set<string>()
  );

  /** Grid filter mode: 'boundingBox' filters to viewport, 'global' fetches all data. */
  private readonly _gridFilterMode: WritableSignal<GridScopeFilterMode> = signal<GridScopeFilterMode>(
    this.gridFeatures.scopeToggle ? 'boundingBox' : 'global'
  );

  /** Active column filters, synced from persisted store state after rehydration. */
  private readonly _columnFilters: WritableSignal<ColumnFilterMap> = signal<ColumnFilterMap>(
    new Map<string, ColumnFilter>()
  );

  /**
   * Entity attribute field → canonical field lookup, sourced from the
   * ELEMENT_GRID preference. Drives row coalescing in `gridData`.
   */
  private readonly _remappingLookup: WritableSignal<ReadonlyMap<string, string>> =
    signal<ReadonlyMap<string, string>>(EMPTY_REMAPPING_LOOKUP);

  /**
   * Column remapping definitions sourced from the ELEMENT_GRID preference.
   * Drives the remapped-field set used to route sort/filter requests against
   * canonical attribute names. The backend resolves the per-entity-type JSONB
   * key via the server-side `IAttributeAliasResolver` (seeded from the same
   * Preferences catalog), so the client no longer ships an `attributeAliases`
   * payload alongside the elements query.
   */
  private readonly _columnRemappings: WritableSignal<ReadonlyArray<GridColumnRemapping>> = signal<
    ReadonlyArray<GridColumnRemapping>
  >(new Array<GridColumnRemapping>());

  /**
   * Slug and display-name lookups from grid preferences. Used to align summary-tree
   * tile-layer ids (e.g. `pms_inventory`) with remapping `elementType` keys
   * (e.g. `Pavement_Section_Primary`).
   */
  private readonly _discriminatorLabels: WritableSignal<Record<string, string>> = signal<
    Record<string, string>
  >({});

  /** Stores the last `where` clause so `loadNextGridPage()` can reuse the same filter. */
  private lastGridWhere: Record<string, unknown> = {};

  /** Full primary group summary from the server (all pages accumulated). */
  private allPrimaryGroupSummaries: Array<GridGroupSummaryItem> = [];

  /** Cursor for the next page of primary group headers (paginated summary). */
  private readonly _groupSummaryEndCursor: WritableSignal<string | null> = signal<string | null>(null);

  /** Whether more primary group header pages are available. */
  private readonly _groupSummaryHasNextPage: WritableSignal<boolean> = signal<boolean>(false);

  /** Server-reported distinct primary bucket count from the paginated summary API. */
  private primarySummaryTotalDistinctCount: number = 0;

  /** Number of primary group header pages loaded so far. */
  private readonly _groupHeaderPagesLoaded: WritableSignal<number> = signal<number>(1);

  /** Pending bucket paths queued for a batched row fetch. */
  private readonly batchLoadQueue: Set<string> = new Set<string>();

  /** Debounce timer handle for {@link flushBatchLoadQueue}. */
  private batchLoadTimer: ReturnType<typeof setTimeout> | null = null;

  /** Tracked timers for staggered batch-fallback bucket loads. */
  private readonly batchFallbackTimers: Set<ReturnType<typeof setTimeout>> = new Set<
    ReturnType<typeof setTimeout>
  >();

  /** In-flight "load more" requests; `_gridLoadingMore` stays true until zero. */
  private gridLoadingMoreRefCount: number = 0;

  /**
   * Monotonic counter incremented on each new grid query. Async pagination
   * callbacks capture the generation at request start and no-op when stale.
   */
  private queryGeneration: number = 0;

  /**
   * Fingerprint of the last query parameters emitted to `elementsQuery$`.
   * Used to skip duplicate queries when the effect re-fires without
   * meaningful parameter changes (e.g. sub-pixel bounding-box drift).
   */
  private lastQueryFingerprint: string = '';

  // ── Read-only public signals ────────────────────────────────────────

  /** Loaded elements data (read-only). */
  public readonly elements: Signal<GridRowsPage | null> = this._elements.asReadonly();

  /** Error message (read-only). */
  public readonly elementsError: Signal<string | null> = this._elementsError.asReadonly();

  /** Whether the initial grid data fetch is in progress (read-only). */
  public readonly gridLoading: Signal<boolean> = this._gridLoading.asReadonly();

  /** Whether the group-summary / discovery phase is in progress (read-only). */
  public readonly gridSummaryLoading: Signal<boolean> = this._gridSummaryLoading.asReadonly();

  /** Warnings when grouped data loading was truncated by orchestration caps (read-only). */
  public readonly groupingCapWarnings: Signal<Array<GroupingCapWarning>> =
    this._groupingCapWarnings.asReadonly();

  /** Whether more primary group header pages can be loaded (read-only). */
  public readonly hasMoreGroupHeaders: Signal<boolean> = computed((): boolean => {
    return (
      hasUnrevealedPrimaryGroupHeaders(this.buildGroupSummaryHost()) ||
      canFetchMorePrimaryGroupSummaries(this.buildGroupSummaryHost())
    );
  });

  /** Whether more pages are available (read-only). */
  public readonly gridHasMore: Signal<boolean> = this._gridHasMore.asReadonly();

  /** Whether a load-more request is in flight (read-only). */
  public readonly gridLoadingMore: Signal<boolean> = this._gridLoadingMore.asReadonly();

  /** Per-bucket loading / pagination flags for grouped leaf rendering (read-only). */
  public readonly groupBucketStates: Signal<Record<string, DataGridGroupBucketViewState>> = computed(
    (): Record<string, DataGridGroupBucketViewState> => {
      const pagination: Map<string, GroupPaginationState> | null = this._groupPagination();
      if (pagination === null) {
        return {};
      }
      const states: Record<string, DataGridGroupBucketViewState> = {};
      const groupFields: Array<string> = this._gridGroupByFields();
      const labels: Record<string, string> = this._discriminatorLabels();

      // Several slugs can share one display name, so a path is merged rather than replaced: the
      // group they render as is loading while any of them is, has more while any of them has, and
      // holds as many records as all of them combined.
      const merge = (key: string, next: DataGridGroupBucketViewState): void => {
        const existing: DataGridGroupBucketViewState | undefined = states[key];
        states[key] =
          existing === undefined
            ? next
            : {
                loading: existing.loading || next.loading,
                hasNextPage: existing.hasNextPage || next.hasNextPage,
                totalCount: (existing.totalCount ?? 0) + (next.totalCount ?? 0)
              };
      };

      for (const [path, state] of pagination.entries()) {
        const view: DataGridGroupBucketViewState = {
          loading: state.loading,
          hasNextPage: state.hasNextPage,
          totalCount: state.totalCount
        };
        merge(path, view);

        // Pagination keys buckets by slug while the group tree addresses nodes by display name.
        // Publishing both spellings is what lets the tree resolve a bucket's loading state.
        const displayPath: string = displayDiscriminatorBucketPath(
          path,
          groupFields,
          this.orchestrationOptions.discriminatorField,
          labels
        );
        if (displayPath !== path) {
          merge(displayPath, view);
        }
      }
      return states;
    }
  );

  /** Current grid group-by fields (read-only). */
  public readonly gridGroupByFields: Signal<Array<string>> = this._gridGroupByFields.asReadonly();

  /** The outermost group field (read-only). */
  public readonly primaryGroupField: Signal<string | null> = this._primaryGroupField.asReadonly();

  /** Sub-group counts (read-only). */
  public readonly subGroupCounts: Signal<Record<string, number>> = this._subGroupCounts.asReadonly();

  /** Group fields detected as high-cardinality (collapsed + lazy-loaded by default). */
  public readonly highCardinalityFields: Signal<Set<string>> = this._highCardinalityFields.asReadonly();

  /** Grid filter mode (read-only). */
  public readonly gridFilterMode: Signal<GridScopeFilterMode> = this._gridFilterMode.asReadonly();

  /** Active column filters (read-only). */
  public readonly columnFilters: Signal<ColumnFilterMap> = this._columnFilters.asReadonly();

  /** Number of active column filters. */
  public readonly activeFilterCount: Signal<number> = computed((): number => {
    let count: number = 0;
    for (const filter of this._columnFilters().values()) {
      if (isFilterActive(filter)) {
        count++;
      }
    }
    return count;
  });

  // ── Computed signals ────────────────────────────────────────────────

  /** Whether bounding-box filtering is active. */
  public readonly boundingBoxActive: Signal<boolean> = computed(
    (): boolean => this._gridFilterMode() === 'boundingBox'
  );

  /**
   * Grid data derived from the elements query response.
   * Flattens each element's top-level fields + attributes into a single record per row.
   */
  public readonly gridData: Signal<Array<Record<string, unknown>>> = computed(
    (): Array<Record<string, unknown>> => {
      const response: GridRowsPage | null = this._elements();
      if (response === null || response.nodes.length === 0) {
        return new Array<Record<string, unknown>>();
      }
      // Hoist the remapping lookup read out of the per-row .map() so signal
      // tracking only runs once per gridData recompute. With up to 50 parallel
      // groups × 100-row pages, the row count can exceed 5,000 per refresh and
      // a per-row signal read adds up.
      const lookup: ReadonlyMap<string, string> = this._remappingLookup();
      const hasRemappings: boolean = lookup.size > 0;

      // Deduplicate by element id to prevent NG0955 duplicate-key warnings.
      // Races between the query pipeline and per-group merge can briefly produce
      // duplicate nodes in _elements; keeping only the first occurrence is safe
      // because later pages always contain newer data for a given id.
      const seen: Set<string> = new Set<string>();
      return response.nodes
        .filter((raw: GridRow): boolean => {
          const el = asGridSourceRow(raw);
          const id = el.id;
          if (id === undefined || seen.has(id)) {
            return false;
          }
          seen.add(id);
          return true;
        })
        .map((raw: GridRow): Record<string, unknown> => {
          const el = asGridSourceRow(raw);
          const attrs: Readonly<Record<string, unknown>> = el.attributes ?? EMPTY_ATTRS;
          const row: Record<string, unknown> = {};

          // Single-pass copy + canonical coalesce. `for...in` over a plain
          // JSON-derived object avoids the [k, v] tuple allocations that
          // `Object.entries` performs. When no remappings are configured we
          // fall back to `Object.assign`, which is well-optimised in modern
          // engines and matches the previous `...attrs` semantics. Maps an
          // entity-specific attribute (e.g. B_W_01_YEAR_BUILT) onto the
          // canonical remapped field (e.g. yearBuilt) so the synthesised
          // shared column renders a value for every entity type.
          if (hasRemappings) {
            for (const attrKey in attrs) {
              const attrValue: unknown = attrs[attrKey];
              row[attrKey] = attrValue;
              const canonical: string | undefined = lookup.get(attrKey);
              if (canonical !== undefined && row[canonical] === undefined) {
                row[canonical] = attrValue;
              }
            }
          } else {
            Object.assign(row, attrs);
          }

          // System fields from GraphQL must win over same-named attribute keys.
          applyElementTopLevelFields(row, el);

          return row;
        });
    }
  );

  /** Total count of elements matching the current filters. */
  public readonly gridTotalCount: Signal<number> = computed((): number => {
    const pagination: Map<string, GroupPaginationState> | null = this._groupPagination();
    const primaryField: string | null = this._primaryGroupField();
    const groupFields: Array<string> = this._gridGroupByFields();
    if (
      pagination !== null &&
      primaryField !== null &&
      groupFields.length > 0 &&
      primaryField !== this.orchestrationOptions.discriminatorField
    ) {
      const bucketTotal: number = sumPrimaryBucketTotals(pagination);
      if (bucketTotal > 0) {
        return bucketTotal;
      }
    }
    return this._elements()?.totalCount ?? 0;
  });

  /** Server-provided totalCount per element type, for accurate group header counts. */
  public readonly typeTotalCounts: Signal<Record<string, number>> = computed((): Record<string, number> => {
    const pagination: Map<string, GroupPaginationState> | null = this._groupPagination();
    if (pagination === null) {
      return {};
    }
    const counts: Record<string, number> = {};
    const primaryField: string | null = this._primaryGroupField();
    const groupFields: Array<string> = this._gridGroupByFields();
    const headerPageLimit: number = GROUP_HEADER_PAGE_SIZE * this._groupHeaderPagesLoaded();

    // Primary-level headers only — nested composite keys contain the path delimiter.
    const primaryEntries: Array<[string, GroupPaginationState]> = Array.from(pagination.entries()).filter(
      ([key]: [string, GroupPaginationState]): boolean => !key.includes(GROUP_PATH_DELIMITER)
    );

    // When grouping by a non-elementType primary, paginate displayed headers client-side
    // (or from server-paginated summary pages).
    if (
      primaryField !== null &&
      groupFields.length > 0 &&
      primaryField !== this.orchestrationOptions.discriminatorField
    ) {
      const sorted: Array<[string, GroupPaginationState]> = primaryEntries.sort(
        ([, stateA]: [string, GroupPaginationState], [, stateB]: [string, GroupPaginationState]): number =>
          stateB.totalCount - stateA.totalCount
      );
      const displayLimit: number = Math.min(sorted.length, headerPageLimit);
      for (const [key, state] of sorted.slice(0, displayLimit)) {
        counts[key] = state.totalCount;
      }
      return counts;
    }

    const labels: Record<string, string> = this._discriminatorLabels();
    const primaryKeys: Set<string> = new Set<string>(
      primaryEntries.map(([key]: [string, GroupPaginationState]): string => key)
    );
    for (const [key, state] of primaryEntries) {
      if (isAliasedDiscriminatorPaginationKey(key, primaryKeys, labels)) {
        continue;
      }
      counts[key] = state.totalCount;
      const displayName: string | undefined = labels[key];
      if (displayName !== undefined && displayName !== key) {
        counts[displayName] = state.totalCount;
      }
    }
    return counts;
  });

  // ── Initialization ──────────────────────────────────────────────────

  constructor() {
    this.destroyRef.onDestroy((): void => {
      this.clearBatchFallbackTimers();
    });

    // Subscribe to config when a host provides GRID_CONFIG_SOURCE.
    if (this.configSource !== null) {
      this.configSource.config$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((preference: GridConfig): void => {
          this._remappingLookup.set(preference.remappingLookup);
          this._columnRemappings.set(preference.columnRemappings);
          this._discriminatorLabels.set(preference.discriminatorLabels);
        });
    }
  }

  /**
   * Wires up the debounced query pipeline and the store rehydration sync.
   * Must be called from the component constructor.
   *
   * @param selectedElementTypes - Signal providing selected element type strings.
   * @param showGrid - Signal indicating whether the grid panel is open.
   * @param mapBounds - Signal providing current map viewport bounding box, or null when scope toggle is disabled.
   * @param gridSharedColumns - Signal providing shared column definitions.
   * @param gridTypeSpecificColumns - Signal providing type-specific column definitions.
   */
  public initialize(
    selectedElementTypes: Signal<Array<string>>,
    showGrid: Signal<boolean>,
    mapBounds: Signal<{ minX: number; minY: number; maxX: number; maxY: number } | null> | null,
    gridSharedColumns: Signal<Array<DataGridColumn>>,
    gridTypeSpecificColumns: Signal<Record<string, Array<DataGridColumn>>>
  ): void {
    this.selectedElementTypesRef = selectedElementTypes;
    this.showGridRef = showGrid;
    this.sharedColumnsRef = gridSharedColumns;
    this.discriminatorColumnsRef = gridTypeSpecificColumns;
    this.setupElementsQueryPipeline(selectedElementTypes);
    this.setupElementsQueryEffect(selectedElementTypes, showGrid, mapBounds);
    this.setupGridGroupingSync();
    this.setupColumnFilterSync();
    this.setupColumnFilterCleanup(selectedElementTypes, showGrid);
  }

  /** Cached reference to the selected element types signal. */
  private selectedElementTypesRef: Signal<Array<string>> | null = null;

  /** Cached reference to the showGrid signal. */
  private showGridRef: Signal<boolean> | null = null;

  /** Cached reference to shared columns signal for sort resolution. */
  private sharedColumnsRef: Signal<Array<DataGridColumn>> | null = null;

  /** Cached reference to type-specific columns signal for sort resolution. */
  private discriminatorColumnsRef: Signal<Record<string, Array<DataGridColumn>>> | null = null;

  /**
   * Set of canonical remapped field names. These live in `sharedColumns` for
   * rendering but are JSONB attributes for query purposes, so sort/filter
   * logic must treat them as attributes. The backend resolves the per-entity
   * JSONB key from its own alias catalog when the canonical name reaches
   * `attributeFilter` / unified `sort`.
   *
   * Memoised against `_columnRemappings` so the hot-path callers
   * (effect, `buildCurrentAttributeFilters`, `splitWhereByCanonicalAlias`) —
   * which can fire ~100× per grouped refresh — share a single Set instead of
   * each rebuilding their own.
   */
  private readonly remappedFields: Signal<ReadonlySet<string>> = computed((): ReadonlySet<string> => {
    const remappings: ReadonlyArray<GridColumnRemapping> = this._columnRemappings();
    return remappings.length === 0
      ? EMPTY_REMAPPED_FIELDS
      : new Set<string>(remappings.map((r: GridColumnRemapping): string => r.canonicalField));
  });

  // ── Query Pipeline ──────────────────────────────────────────────────

  private setupElementsQueryPipeline(selectedElementTypes: Signal<Array<string>>): void {
    subscribeElementsQueryPipeline(
      this.buildQueryPipelineHost(),
      this.elementsQuery$,
      selectedElementTypes,
      this.destroyRef
    );
  }

  /** Builds the delegate passed to the extracted query pipeline module. */
  private buildQueryPipelineHost(): ElementsQueryPipelineHost {
    return {
      resetForNewQuery: (where: Record<string, unknown>): void => {
        this.queryGeneration++;
        this.lastGridWhere = where;
        this._subGroupCounts.set({});
        this._highCardinalityFields.set(new Set<string>());
        this._groupingCapWarnings.set(new Array<GroupingCapWarning>());
        this.allPrimaryGroupSummaries = [];
        this._groupSummaryEndCursor.set(null);
        this._groupSummaryHasNextPage.set(false);
        this.primarySummaryTotalDistinctCount = 0;
        this._groupHeaderPagesLoaded.set(1);
        this.batchLoadQueue.clear();
        if (this.batchLoadTimer !== null) {
          clearTimeout(this.batchLoadTimer);
          this.batchLoadTimer = null;
        }
        this.clearBatchFallbackTimers();
        this.resetGridLoadingMoreRefCount();
        this._groupPagination.set(null);
        this._elements.set(null);
        const groupFields: Array<string> = this._gridGroupByFields();
        const requiresGroupSummary: boolean =
          groupFields.length > 0 && groupFields[0] !== this.orchestrationOptions.discriminatorField;
        this._gridSummaryLoading.set(requiresGroupSummary);
      },
      getGridGroupByFields: (): Array<string> => this._gridGroupByFields(),
      getDiscriminatorField: (): string => this.orchestrationOptions.discriminatorField,
      setPrimaryGroupField: (field: string | null): void => {
        this._primaryGroupField.set(field);
      },
      setGroupPagination: (pagination: Map<string, GroupPaginationState> | null): void => {
        this._groupPagination.set(pagination);
      },
      setGridLoading: (loading: boolean): void => {
        this._gridLoading.set(loading);
      },
      setGridSummaryLoading: (loading: boolean): void => {
        this._gridSummaryLoading.set(loading);
      },
      setElementsError: (message: string): void => {
        this._elementsError.set(message);
      },
      setElements: (response: GridRowsPage): void => {
        this._elements.set(response);
        this.rowObserver?.onRowsLoaded(response.nodes);
      },
      mergeGroupPaginationIntoElements: (): void => {
        this.mergeGroupPaginationIntoElements();
      },
      setGridEndCursor: (cursor: string | null): void => {
        this._gridEndCursor.set(cursor);
      },
      setGridHasMore: (hasMore: boolean): void => {
        this._gridHasMore.set(hasMore);
      },
      getGroupPagination: (): Map<string, GroupPaginationState> | null => this._groupPagination(),
      addHighCardinalityField: (field: string): void => {
        this._highCardinalityFields.update((current: Set<string>): Set<string> =>
          new Set<string>(current).add(field)
        );
      },
      reportGroupingCapExceeded: (warning: GroupingCapWarning): void => {
        this.mergeGroupingCapWarning(warning);
      },
      fetchPerTypeFirstPages: (
        types: Signal<Array<string>>,
        where: Record<string, unknown>
      ): Observable<GridRowsPage> => this.fetchPerTypeFirstPages(types, where),
      discoverSubGroupsViaTree: (
        types: Signal<Array<string>>,
        groupFields: Array<string>,
        baseWhere: Record<string, unknown>,
        initialResponse: GridRowsPage
      ): Observable<GridRowsPage> =>
        this.discoverSubGroupsViaTree(types, groupFields, baseWhere, initialResponse),
      fetchAllPrimaryGroupSummaries: (
        primaryGroup: string,
        types: Array<string>,
        where: Record<string, unknown>
      ): Observable<Array<GridGroupSummaryItem>> =>
        this.fetchAllPrimaryGroupSummaries(primaryGroup, types, where),
      fetchGridPage: (where: Record<string, unknown>, after?: string): Observable<GridRowsPage> =>
        this.fetchGridPage(where, after),
      fetchPerGroupFirstPages: (
        primaryGroup: string,
        groups: Array<GridGroupSummaryItem>,
        where: Record<string, unknown>
      ): Observable<GridRowsPage> => this.fetchPerGroupFirstPages(primaryGroup, groups, where),
      seedPrimaryGroupSkeletonWithHybridEager: (
        primaryGroup: string,
        groups: Array<GridGroupSummaryItem>,
        where: Record<string, unknown>
      ): Observable<GridRowsPage> => this.seedPrimaryGroupSkeletonWithHybridEager(primaryGroup, groups, where)
    };
  }

  /** Retains one cap warning per kind; within a kind, keeps the highest skipped count. */
  private mergeGroupingCapWarning(next: GroupingCapWarning): void {
    this._groupingCapWarnings.update((current: Array<GroupingCapWarning>): Array<GroupingCapWarning> =>
      mergeGroupingCapWarnings(current, next)
    );
  }

  /** Builds shared host context for extracted orchestration module adapters. */
  private createHostContext(): GridOrchestrationHostContext {
    return {
      elementsProvider: this.elementsProvider,
      destroyRef: this.destroyRef,
      getQueryGeneration: (): number => this.queryGeneration,
      getLastGridWhere: (): Record<string, unknown> => this.lastGridWhere,
      getGroupPagination: (): Map<string, GroupPaginationState> | null => this._groupPagination(),
      setGroupPagination: (pagination: Map<string, GroupPaginationState> | null): void => {
        this._groupPagination.set(pagination);
      },
      updateGroupPagination: (
        updater: (current: Map<string, GroupPaginationState>) => Map<string, GroupPaginationState>
      ): void => {
        this._groupPagination.update(
          (current: Map<string, GroupPaginationState> | null): Map<string, GroupPaginationState> | null => {
            if (current === null) {
              return null;
            }
            return updater(current);
          }
        );
      },
      getSubGroupCounts: (): Record<string, number> => this._subGroupCounts(),
      setSubGroupCounts: (counts: Record<string, number>): void => {
        this._subGroupCounts.set(counts);
      },
      getGridGroupByFields: (): Array<string> => this._gridGroupByFields(),
      getGridEndCursor: (): string | null => this._gridEndCursor(),
      setGridEndCursor: (cursor: string | null): void => {
        this._gridEndCursor.set(cursor);
      },
      getGridLoadingMore: (): boolean => this._gridLoadingMore(),
      beginGridLoadingMore: (): void => {
        this.beginGridLoadingMore();
      },
      endGridLoadingMore: (): void => {
        this.endGridLoadingMore();
      },
      setGridHasMore: (hasMore: boolean): void => {
        this._gridHasMore.set(hasMore);
      },
      setElementsError: (message: string | null): void => {
        this._elementsError.set(message);
      },
      getElements: (): GridRowsPage | null => this._elements(),
      setElements: (response: GridRowsPage): void => {
        this._elements.set(response);
        this.rowObserver?.onRowsLoaded(response.nodes);
      },
      addToBatchLoadQueue: (path: string): void => {
        this.batchLoadQueue.add(path);
      },
      takeBatchLoadQueueSnapshot: (): Array<string> => {
        const paths: Array<string> = Array.from(this.batchLoadQueue);
        this.batchLoadQueue.clear();
        return paths;
      },
      scheduleBatchFlush: (flush: () => void): void => {
        if (this.batchLoadTimer !== null) {
          clearTimeout(this.batchLoadTimer);
        }
        this.batchLoadTimer = setTimeout((): void => {
          this.batchLoadTimer = null;
          flush();
        }, BATCH_FLUSH_MS);
      },
      scheduleBatchFallbackLoad: (path: string, delayMs: number, queryGeneration: number): void => {
        this.scheduleBatchFallbackLoad(path, delayMs, queryGeneration);
      },
      mergeAttributeFilters: (
        extra?: Array<GridBackendAttributeFilter>
      ): Array<GridBackendAttributeFilter> | undefined =>
        mergeAttributeFilterEntries(
          this._columnFilters(),
          this.sharedColumnsRef?.() ?? [],
          this.discriminatorColumnsRef?.() ?? {},
          this.remappedFields(),
          extra
        ),
      remappedFields: (): ReadonlySet<string> => this.remappedFields(),
      resolveColumnType: (field: string): GridBackendAttributeFilterType | undefined =>
        resolveColumnDataType(field, this.sharedColumnsRef?.() ?? [], this.discriminatorColumnsRef?.() ?? {}),
      buildNullOperatorEntry: buildNullOperatorEntry,
      getPrimaryGroupField: (): string | null => this._primaryGroupField(),
      getDiscriminatorField: (): string => this.orchestrationOptions.discriminatorField,
      addHighCardinalityField: (field: string): void => {
        this._highCardinalityFields.update((current: Set<string>): Set<string> =>
          new Set<string>(current).add(field)
        );
      },
      reportGroupingCapExceeded: (warning: GroupingCapWarning): void => {
        this.mergeGroupingCapWarning(warning);
      },
      fetchGridPage: (where: Record<string, unknown>, after?: string): Observable<GridRowsPage> =>
        this.fetchGridPage(where, after),
      fetchNullBucketViaClientFilter: (
        path: string,
        bucketWhere: Record<string, unknown>,
        scanAfter: string | undefined,
        pageSize: number,
        expectedTotal: number,
        alreadyLoaded: number
      ): Observable<GridRowsPage> =>
        this.fetchNullBucketViaClientFilter(
          path,
          bucketWhere,
          scanAfter,
          pageSize,
          expectedTotal,
          alreadyLoaded
        ),
      buildWhereFromPath: (path: string): Record<string, unknown> => this.buildWhereFromPath(path),
      buildBucketInputFromPath: (path: string): GridBucketScope => this.buildBucketInputFromPath(path),
      buildBatchBucketRequestOptions: (
        buckets: Array<GridBucketScope>
      ): ReturnType<GridOrchestrationService['buildBatchBucketRequestOptions']> =>
        this.buildBatchBucketRequestOptions(buckets),
      isNullBucketPath: (path: string): boolean => this.isNullBucketPath(path),
      isEmptyElementsResponse: (response: GridRowsPage): boolean => this.isEmptyElementsResponse(response),
      logEmptyNodeMismatch: (bucketKey: string, response: GridRowsPage): void => {
        this.logEmptyNodeMismatch(bucketKey, response);
      },
      applyBucketFetchResult: (
        bucketKey: string,
        response: GridRowsPage,
        options?: { clientNullFilter?: boolean; cursorSource?: 'batch' | 'elements' }
      ): void => {
        runApplyBucketFetchResult(this.buildPaginationHost(), bucketKey, response, options);
      }
    };
  }

  /**
   * Emits into `elementsQuery$` whenever filter parameters, sort order, or
   * grouping mode change.
   */
  private setupElementsQueryEffect(
    selectedElementTypes: Signal<Array<string>>,
    showGrid: Signal<boolean>,
    mapBounds: Signal<{ minX: number; minY: number; maxX: number; maxY: number } | null> | null
  ): void {
    effect((): void => {
      const selectedTypes: Array<string> = selectedElementTypes();
      const filterMode: GridScopeFilterMode = this._gridFilterMode();
      void this._gridSort();
      void this._gridGroupByFields();

      // Read column filters so changes trigger a re-query.
      const columnFilters: ColumnFilterMap = this._columnFilters();

      if (!showGrid()) {
        return;
      }

      if (selectedTypes.length === 0) {
        this._elements.set(null);
        this._gridLoading.set(false);
        return;
      }

      const where: Record<string, unknown> = {};
      const discriminatorField: string = this.orchestrationOptions.discriminatorField;
      if (discriminatorField.length > 0) {
        where[discriminatorField] = { in: selectedTypes };
      }

      if (filterMode === 'boundingBox' && mapBounds !== null) {
        const bounds: { minX: number; minY: number; maxX: number; maxY: number } | null = mapBounds();
        if (bounds !== null) {
          where['shape'] = {
            intersects: {
              geometry: {
                type: 'Polygon',
                coordinates: [
                  [
                    [bounds.minX, bounds.minY],
                    [bounds.maxX, bounds.minY],
                    [bounds.maxX, bounds.maxY],
                    [bounds.minX, bounds.maxY],
                    [bounds.minX, bounds.minY]
                  ]
                ]
              }
            }
          };
        }
      }
      // Merge shared-column filters into the where clause.
      if (columnFilters.size > 0 && this.sharedColumnsRef !== null) {
        const filterWhere: Record<string, unknown> = buildWhereFromFilters(
          columnFilters,
          this.sharedColumnsRef(),
          this.remappedFields()
        );
        Object.assign(where, filterWhere);
      }

      // Deduplicate: skip the query if all parameters match the last emission.
      // This prevents redundant fetches when the effect re-fires from signal
      // dependencies that did not meaningfully change the query.
      const queryFingerprint: string = JSON.stringify({
        where,
        // Attribute filters (remapped canonical + type-specific columns) are applied
        // in fetchGridPage, not in `where`, so include column filters in the fingerprint.
        columnFilters: serializeFilters(columnFilters),
        sort: this._gridSort(),
        groupByFields: this._gridGroupByFields()
      });
      if (queryFingerprint === this.lastQueryFingerprint) {
        return;
      }
      this.lastQueryFingerprint = queryFingerprint;

      this._gridLoading.set(true);
      this.elementsQuery$.next(where);
    });
  }

  /**
   * After store rehydration, syncs `gridGroupByFields` with the persisted value.
   */
  private setupGridGroupingSync(): void {
    let groupingSynced: boolean = false;
    effect((): void => {
      if (!this.persistence.ready() || groupingSynced) {
        return;
      }
      groupingSynced = true;
      this._gridGroupByFields.set(this.persistence.state().groupByFields);
    });
  }

  /**
   * After store rehydration, syncs `_columnFilters` with the persisted value.
   */
  private setupColumnFilterSync(): void {
    let filtersSynced: boolean = false;
    effect((): void => {
      if (!this.persistence.ready() || filtersSynced) {
        return;
      }
      filtersSynced = true;
      const persisted: ColumnFilterMap = deserializeFilters(this.persistence.state().columnFilters);
      if (persisted.size > 0) {
        this._columnFilters.set(persisted);
      }
    });
  }

  /**
   * Cleans up any column filters that belong to element types that are no longer selected.
   * Runs as an effect whenever selectedElementTypes, column definitions, or the grid visibility change.
   *
   * @param selectedElementTypes - Signal providing selected element type strings.
   * @param showGrid - Signal indicating whether the grid panel is open.
   */
  private setupColumnFilterCleanup(
    selectedElementTypes: Signal<Array<string>>,
    showGrid: Signal<boolean>
  ): void {
    effect((): void => {
      if (!showGrid() || !this.persistence.ready()) {
        return;
      }
      const shared: Array<DataGridColumn> = this.sharedColumnsRef?.() ?? [];
      if (shared.length === 0) {
        return;
      }
      const selectedTypes: Array<string> = selectedElementTypes();
      const typeSpecific: Record<string, Array<DataGridColumn>> = this.discriminatorColumnsRef?.() ?? {};
      const currentFilters: ColumnFilterMap = this._columnFilters();

      if (currentFilters.size === 0) {
        return;
      }

      // Build a set of all valid/active fields based on selected types
      const activeFields: Set<string> = new Set<string>(
        shared.map((col: DataGridColumn): string => col.field)
      );
      for (const type of selectedTypes) {
        const cols: Array<DataGridColumn> = typeSpecific[type] ?? [];
        for (const col of cols) {
          activeFields.add(col.field);
        }
      }

      // Filter out stale/dead fields
      let hasStale: boolean = false;
      const nextFilters: ColumnFilterMap = new Map<string, ColumnFilter>();
      for (const [field, filter] of currentFilters.entries()) {
        if (activeFields.has(field)) {
          nextFilters.set(field, filter);
        } else {
          hasStale = true;
        }
      }

      if (hasStale) {
        this._columnFilters.set(nextFilters);
        this.persistence.save({
          ...this.persistence.state(),
          columnFilters: serializeFilters(nextFilters)
        });
      }
    });
  }

  // ── Data Fetching ───────────────────────────────────────────────────

  /** Fetches a single page of elements, handling null-bucket fallback logic. */
  private fetchGridPage(where: Record<string, unknown>, after?: string): Observable<GridRowsPage> {
    return runFetchGridPage(
      this.buildNullBucketHost(),
      (msg: string): void => logGridOrchestration(GridOrchestrationLogLevel.Warn, msg),
      where,
      after
    );
  }

  /** Whether a bucket path includes a null/Unknown segment for any grouped field. */
  private isNullBucketPath(path: string): boolean {
    return checkIsNullBucketPath(path, this._gridGroupByFields());
  }

  /** Whether the response contains no rows and reports zero matches. */
  private isEmptyElementsResponse(response: GridRowsPage): boolean {
    return checkIsEmptyElementsResponse(response);
  }

  /**
   * Loads null-bucket rows by paginating unfiltered `elements` queries and matching
   * bucket segments client-side. Used when backend `IS_NULL` attribute filters return empty.
   */
  private fetchNullBucketViaClientFilter(
    path: string,
    bucketWhere: Record<string, unknown>,
    scanAfter: string | undefined,
    pageSize: number,
    expectedTotal: number,
    alreadyLoaded: number
  ): Observable<GridRowsPage> {
    return runFetchNullBucketViaClientFilter(
      this.buildNullBucketHost(),
      path,
      bucketWhere,
      scanAfter,
      pageSize,
      expectedTotal,
      alreadyLoaded
    );
  }

  /**
   * Fires parallel per-type first-page queries when grouped by elementType.
   */
  private fetchPerTypeFirstPages(
    selectedElementTypes: Signal<Array<string>>,
    baseWhere: Record<string, unknown>
  ): Observable<GridRowsPage> {
    const types: Array<string> = selectedElementTypes();
    const queries: Array<[string, Observable<GridRowsPage>]> = [];

    for (const type of types) {
      const typeWhere: Record<string, unknown> = { ...baseWhere };
      const discriminatorField: string = this.orchestrationOptions.discriminatorField;
      if (discriminatorField.length > 0) {
        typeWhere[discriminatorField] = { in: [type] };
      }
      queries.push([
        type,
        this.fetchGridPage(typeWhere).pipe(
          catchError((err: Error): Observable<GridRowsPage> => {
            logGridOrchestration(
              GridOrchestrationLogLevel.Error,
              `Failed to load elements for type '${type}':`,
              err
            );
            return of({ nodes: [], totalCount: 0 });
          })
        )
      ]);
    }

    return forkJoinWithConcurrency(queries, MAX_REQUEST_CONCURRENCY).pipe(
      map((results: Record<string, GridRowsPage>): GridRowsPage => {
        const paginationMap: Map<string, GroupPaginationState> = new Map<string, GroupPaginationState>();
        const allNodes: Array<GridRow> = [];
        let totalCount: number = 0;

        for (const [type, response] of Object.entries(results)) {
          paginationMap.set(type, {
            nodes: [...response.nodes],
            endCursor: response.pageInfo?.endCursor ?? null,
            hasNextPage: response.pageInfo?.hasNextPage ?? false,
            totalCount: response.totalCount ?? response.nodes.length,
            loading: false,
            pagesLoaded: 1,
            cursorUsableForElements: true
          });
          allNodes.push(...response.nodes);
          totalCount += response.totalCount ?? response.nodes.length;
        }

        this._groupPagination.set(paginationMap);
        return { nodes: allNodes, totalCount };
      })
    );
  }

  /**
   * Fetches every page of the primary group summary before seeding headers.
   * A single page (50 buckets) is not enough for high-cardinality primaries such as
   * `yearBuilt`, which would otherwise show a partial total and missing year headers.
   *
   * @param field - Primary group field.
   * @param elementTypes - Selected element types.
   * @param where - Base filter.
   */
  private fetchAllPrimaryGroupSummaries(
    field: string,
    elementTypes: Array<string>,
    where: Record<string, unknown>
  ): Observable<Array<GridGroupSummaryItem>> {
    return runFetchAllPrimaryGroupSummaries(this.buildGroupSummaryHost(), field, elementTypes, where);
  }

  private beginGridLoadingMore(): void {
    this.gridLoadingMoreRefCount++;
    if (this.gridLoadingMoreRefCount === 1) {
      this._gridLoadingMore.set(true);
    }
  }

  /** Decrements the in-flight load-more counter and clears the signal at zero. */
  private endGridLoadingMore(): void {
    this.gridLoadingMoreRefCount = Math.max(0, this.gridLoadingMoreRefCount - 1);
    if (this.gridLoadingMoreRefCount === 0) {
      this._gridLoadingMore.set(false);
    }
  }

  /** Resets the load-more counter when a new grid query starts. */
  private resetGridLoadingMoreRefCount(): void {
    this.gridLoadingMoreRefCount = 0;
    this._gridLoadingMore.set(false);
  }

  /** Schedules a staggered single-bucket retry after batch API failure. */
  private scheduleBatchFallbackLoad(path: string, delayMs: number, queryGeneration: number): void {
    const timer: ReturnType<typeof setTimeout> = setTimeout((): void => {
      this.batchFallbackTimers.delete(timer);
      if (this.queryGeneration !== queryGeneration) {
        return;
      }
      runLoadBucket(this.buildPaginationHost(), path);
    }, delayMs);
    this.batchFallbackTimers.add(timer);
  }

  /** Cancels all pending batch-fallback timers. */
  private clearBatchFallbackTimers(): void {
    for (const timer of this.batchFallbackTimers) {
      clearTimeout(timer);
    }
    this.batchFallbackTimers.clear();
  }

  /** Builds the host adapter passed to extracted group-discovery helpers. */
  private buildGroupDiscoveryHost(): GroupDiscoveryHost {
    return createGroupDiscoveryHostAdapter(this.createHostContext());
  }

  /** Builds the host adapter passed to extracted bucket-loading helpers. */
  private buildBucketLoadingHost(): BucketLoadingHost {
    return createBucketLoadingHostAdapter(this.createHostContext());
  }

  /** Builds the host adapter passed to extracted pagination helpers. */
  private buildPaginationHost(): GridPaginationHost {
    return createPaginationHostAdapter(this.createHostContext());
  }

  /** Builds the host adapter passed to extracted group-summary helpers. */
  private buildGroupSummaryHost(): GroupSummaryHost {
    return {
      elementsProvider: this.elementsProvider,
      destroyRef: this.destroyRef,
      getColumnFilters: (): ColumnFilterMap => this._columnFilters(),
      getSharedColumns: (): Array<DataGridColumn> => this.sharedColumnsRef?.() ?? [],
      getTypeSpecificColumns: (): Record<string, Array<DataGridColumn>> =>
        this.discriminatorColumnsRef?.() ?? {},
      getRemappedFields: (): ReadonlySet<string> => this.remappedFields(),
      getLastGridWhere: (): Record<string, unknown> => this.lastGridWhere,
      getGroupPagination: (): Map<string, GroupPaginationState> | null => this._groupPagination(),
      setGroupPagination: (pagination: Map<string, GroupPaginationState> | null): void => {
        this._groupPagination.set(pagination);
      },
      updateGroupPagination: (
        updater: (current: Map<string, GroupPaginationState>) => Map<string, GroupPaginationState>
      ): void => {
        this._groupPagination.update(
          (current: Map<string, GroupPaginationState> | null): Map<string, GroupPaginationState> | null => {
            if (current === null) {
              return null;
            }
            return updater(current);
          }
        );
      },
      getPrimaryGroupField: (): string | null => this._primaryGroupField(),
      getDiscriminatorField: (): string => this.orchestrationOptions.discriminatorField,
      getGridGroupByFields: (): Array<string> => this._gridGroupByFields(),
      getGroupSummaryHasNextPage: (): boolean => this._groupSummaryHasNextPage(),
      setGroupSummaryHasNextPage: (value: boolean): void => {
        this._groupSummaryHasNextPage.set(value);
      },
      getGroupSummaryEndCursor: (): string | null => this._groupSummaryEndCursor(),
      setGroupSummaryEndCursor: (cursor: string | null): void => {
        this._groupSummaryEndCursor.set(cursor);
      },
      getGroupHeaderPagesLoaded: (): number => this._groupHeaderPagesLoaded(),
      setGroupHeaderPagesLoaded: (value: number): void => {
        this._groupHeaderPagesLoaded.set(value);
      },
      incrementGroupHeaderPagesLoaded: (): void => {
        this._groupHeaderPagesLoaded.update((current: number): number => current + 1);
      },
      getAllPrimaryGroupSummaries: (): Array<GridGroupSummaryItem> => this.allPrimaryGroupSummaries,
      setAllPrimaryGroupSummaries: (groups: Array<GridGroupSummaryItem>): void => {
        this.allPrimaryGroupSummaries = groups;
      },
      getPrimarySummaryTotalDistinctCount: (): number => this.primarySummaryTotalDistinctCount,
      setPrimarySummaryTotalDistinctCount: (count: number): void => {
        this.primarySummaryTotalDistinctCount = count;
      },
      getQueryGeneration: (): number => this.queryGeneration,
      fetchPerGroupFirstPagesForSkeleton: (
        field: string,
        groups: Array<GridGroupSummaryItem>,
        baseWhere: Record<string, unknown>
      ): Observable<GridRowsPage> => this.fetchPerGroupFirstPages(field, groups, baseWhere),
      mergeGroupPaginationIntoElements: (): void => {
        this.mergeGroupPaginationIntoElements();
      }
    };
  }

  /** Builds the host adapter passed to extracted null-bucket helpers. */
  private buildNullBucketHost(): NullBucketHost {
    return {
      elementsProvider: this.elementsProvider,
      getRemappedFields: (): ReadonlySet<string> => this.remappedFields(),
      getSharedColumns: (): Array<DataGridColumn> => this.sharedColumnsRef?.() ?? [],
      getTypeSpecificColumns: (): Record<string, Array<DataGridColumn>> =>
        this.discriminatorColumnsRef?.() ?? {},
      getColumnFilters: (): ColumnFilterMap => this._columnFilters(),
      getRemappingLookup: (): ReadonlyMap<string, string> => this._remappingLookup(),
      getGridGroupByFields: (): Array<string> => this._gridGroupByFields(),
      getGridSort: (): Array<GridSortField> | null => this._gridSort(),
      getColumnRemappings: (): ReadonlyArray<GridColumnRemapping> => this._columnRemappings(),
      getElementTypeDisplayNames: (): Record<string, string> => this._discriminatorLabels(),
      getDiscriminatorField: (): string => this.orchestrationOptions.discriminatorField
    };
  }

  /**
   * Loads the next page of primary group headers (paginated summary or client slice).
   */
  public loadMoreGroupHeaders(): void {
    runLoadMoreGroupHeaders(this.buildGroupSummaryHost());
  }

  /**
   * Two-phase grouping: loads the first page for each discovered group value,
   * batched via {@link GridDataSource.getElementsForGroupBuckets} when
   * possible (chunks of {@link BATCH_BUCKET_SIZE}).
   */
  private fetchPerGroupFirstPages(
    _field: string,
    groups: Array<GridGroupSummaryItem>,
    _baseWhere: Record<string, unknown>
  ): Observable<GridRowsPage> {
    return runFetchPerGroupFirstPages(this.buildBucketLoadingHost(), groups);
  }

  private fetchBucketsFirstPages(bucketKeys: Array<string>): Observable<GridRowsPage> {
    return fetchBucketsFirstPages(this.buildBucketLoadingHost(), bucketKeys);
  }

  private fetchBucketChunkFirstPages(bucketKeys: Array<string>): Observable<Array<GridBucketRowsPage>> {
    return fetchBucketChunkFirstPages(this.buildBucketLoadingHost(), bucketKeys);
  }

  /**
   * Builds the shared request payload for {@link getElementsForGroupBuckets}.
   */
  private buildBatchBucketRequestOptions(buckets: Array<GridBucketScope>): {
    buckets: Array<GridBucketScope>;
    firstPerBucket: number;
    sharedWhere: Record<string, unknown>;
    attributeFilter?: Array<GridBackendAttributeFilter>;
    sort?: Array<GridSortField>;
  } {
    const columnAttributeFilter: Array<GridBackendAttributeFilter> = buildCurrentAttributeFilters(
      this._columnFilters(),
      this.sharedColumnsRef?.() ?? [],
      this.discriminatorColumnsRef?.() ?? {},
      this.remappedFields()
    );
    return {
      buckets,
      firstPerBucket: BATCH_FIRST_PER_BUCKET,
      sharedWhere: this.lastGridWhere,
      ...(columnAttributeFilter.length > 0 ? { attributeFilter: columnAttributeFilter } : {}),
      ...(this._gridSort() !== null ? { sort: this._gridSort()! } : {})
    };
  }

  /**
   * Applies batch first-page results to `_groupPagination` and returns the
   * aggregated response for the pipeline tap handlers.
   */
  private mergeFirstPageBatchResults(results: Array<GridBucketRowsPage>): GridRowsPage {
    return runMergeFirstPageBatchResults(this.buildBucketLoadingHost(), results);
  }

  /**
   * Seeds `_groupPagination` from a high-cardinality primary group's summary
   * **without fetching any rows**. Each entry carries the server count and is
   * marked `hasNextPage: true` so the grid can lazily load the bucket's first
   * page when it's expanded (via {@link loadGroupPath}). Feeds the primary
   * header counts (`typeTotalCounts`) and lets `discoverSubGroups` iterate the
   * primary values as parents.
   *
   * @param groups - The primary group summary (value + count), count-ordered.
   * @returns An empty response whose `totalCount` is the sum across buckets.
   */
  private seedPrimaryGroupSkeleton(groups: Array<GridGroupSummaryItem>): GridRowsPage {
    return runSeedPrimaryGroupSkeleton(this.buildGroupSummaryHost(), groups);
  }

  /**
   * Seeds high-cardinality primary headers and eagerly loads first pages for
   * buckets whose record count is at or below {@link BUCKET_EAGER_THRESHOLD}.
   */
  private seedPrimaryGroupSkeletonWithHybridEager(
    field: string,
    groups: Array<GridGroupSummaryItem>,
    baseWhere: Record<string, unknown>
  ): Observable<GridRowsPage> {
    return runSeedPrimaryGroupSkeletonWithHybridEager(this.buildGroupSummaryHost(), field, groups, baseWhere);
  }

  /**
   * Logs when the server reports rows exist but returns an empty page (dev aid).
   *
   * @param bucketKey - Bucket identifier for the load.
   * @param response - Elements response from the server.
   */
  private logEmptyNodeMismatch(bucketKey: string, response: GridRowsPage): void {
    const reportedTotal: number = response.totalCount ?? 0;
    if (reportedTotal > 0 && response.nodes.length === 0) {
      logGridOrchestration(
        GridOrchestrationLogLevel.Warn,
        `[GridOrchestration] Bucket '${bucketKey}' reported totalCount=${reportedTotal} but returned 0 nodes. ` +
          `Check attributeFilter / backend filter consistency.`
      );
    }
    if (reportedTotal === 0 && response.nodes.length === 0) {
      const pagination: Map<string, GroupPaginationState> | null = this._groupPagination();
      const seededCount: number | undefined = pagination?.get(bucketKey)?.totalCount;
      if (seededCount !== undefined && seededCount > 0) {
        logGridOrchestration(
          GridOrchestrationLogLevel.Warn,
          `[GridOrchestration] Bucket '${bucketKey}' summary count=${seededCount} but elements query ` +
            `returned 0 rows. Backend IS_NULL / attributeFilter likely not matching summary grouping.`
        );
      }
    }
  }

  private discoverSubGroupsViaTree(
    selectedElementTypes: Signal<Array<string>>,
    groupFields: Array<string>,
    baseWhere: Record<string, unknown>,
    initialResponse: GridRowsPage
  ): Observable<GridRowsPage> {
    return runDiscoverSubGroupsViaTree(
      this.buildGroupDiscoveryHost(),
      selectedElementTypes,
      groupFields,
      baseWhere,
      initialResponse,
      (
        types: Signal<Array<string>>,
        fields: Array<string>,
        where: Record<string, unknown>,
        response: GridRowsPage,
        ancestors?: Array<{ field: string; values: Array<string> }>
      ): Observable<GridRowsPage> => this.discoverSubGroups(types, fields, where, response, ancestors ?? [])
    );
  }

  private discoverSubGroups(
    selectedElementTypes: Signal<Array<string>>,
    remainingGroupFields: Array<string>,
    baseWhere: Record<string, unknown>,
    initialResponse: GridRowsPage,
    ancestorConstraints: Array<{ field: string; values: Array<string> }> = []
  ): Observable<GridRowsPage> {
    return runDiscoverSubGroups(
      this.buildGroupDiscoveryHost(),
      selectedElementTypes,
      remainingGroupFields,
      baseWhere,
      initialResponse,
      ancestorConstraints
    );
  }

  // ── Pagination ──────────────────────────────────────────────────────

  public loadNextGridPage(): void {
    loadNextFlatGridPage(this.buildPaginationHost());
  }

  public loadNextGridPageForGroup(groupValue: string): void {
    this.loadBucket(this.canonicalizeBucketPath(groupValue));
  }

  public onGridLoadMore(path: string | null): void {
    if (path === null || this._groupPagination() === null) {
      this.loadNextGridPage();
      return;
    }
    this.scheduleBucketLoad(this.canonicalizeBucketPath(path));
  }

  public loadGroupPath(path: string): void {
    this.loadBucket(this.canonicalizeBucketPath(path));
  }

  /**
   * Rewrites discriminator display-name path segments to slug pagination keys.
   *
   * @param path - Single-level value or composite `///` path from the grid.
   * @returns Path keyed the same way as `fetchPerTypeFirstPages` buckets.
   */
  private canonicalizeBucketPath(path: string): string {
    return canonicalizeDiscriminatorBucketPath(
      path,
      this._gridGroupByFields(),
      this.orchestrationOptions.discriminatorField,
      this._discriminatorLabels()
    );
  }

  private scheduleBucketLoad(path: string): void {
    runScheduleBucketLoad(this.buildPaginationHost(), path);
  }

  private flushBatchLoadQueue(): void {
    runFlushBatchLoadQueue(this.buildPaginationHost());
  }

  private loadBucket(path: string): void {
    runLoadBucket(this.buildPaginationHost(), path);
  }

  /**
   * Builds a batch API bucket input with alias constraints routed to `attributeFilter`.
   *
   * @param path - Single-level value or composite `///` path.
   */
  private buildBucketInputFromPath(path: string): GridBucketScope {
    const rawWhere: Record<string, unknown> = this.buildWhereFromPath(path);
    const split: {
      where: Record<string, unknown>;
      extraAttributeFilter: Array<GridBackendAttributeFilter>;
      unsupportedAliases: Array<string>;
    } = splitWhereByCanonicalAlias(
      rawWhere,
      this.remappedFields(),
      this.sharedColumnsRef?.() ?? [],
      this.discriminatorColumnsRef?.() ?? {}
    );

    if (split.unsupportedAliases.length > 0) {
      logGridOrchestration(
        GridOrchestrationLogLevel.Warn,
        `buildBucketInputFromPath: dropping unsupported canonical-alias 'where' clause(s) for ` +
          `[${split.unsupportedAliases.join(', ')}].`
      );
    }

    const resolvedExtras: Array<GridBackendAttributeFilter> = resolveNullAliasAttributeFilters(
      split.where,
      split.extraAttributeFilter,
      this.remappedFields(),
      this._columnRemappings(),
      this._discriminatorLabels(),
      this.orchestrationOptions.discriminatorField
    );

    const bucket: GridBucketScope = {
      bucketKey: path,
      where: split.where,
      ...(resolvedExtras.length > 0 ? { attributeFilter: resolvedExtras } : {})
    };
    return bucket;
  }

  /**
   * Builds the scoped `where` clause for a bucket path.
   *
   * @param path - Single-level value or composite `///` path.
   */
  private buildWhereFromPath(path: string): Record<string, unknown> {
    return buildBucketWhereFromPath(
      path,
      this._gridGroupByFields(),
      this._primaryGroupField(),
      this.lastGridWhere,
      this.orchestrationOptions.discriminatorField,
      this._discriminatorLabels()
    );
  }

  private updateGroupPaginationState(groupValue: string, updated: GroupPaginationState): void {
    runUpdateGroupPaginationState(this.buildPaginationHost(), groupValue, updated);
  }

  private mergeGroupPaginationIntoElements(): void {
    runMergeGroupPaginationIntoElements(this.buildPaginationHost());
  }

  // ── Public Methods ──────────────────────────────────────────────────

  /**
   * Re-fires the elements query with the last `where` clause to refresh grid data.
   * Call after an element mutation (create/update/delete) to keep the grid in sync.
   */
  public refreshGrid(): void {
    this._gridLoading.set(true);
    this.elementsQuery$.next(this.lastGridWhere);
  }

  /**
   * Turns a selection descriptor into the live grid query so consumers can
   * run bulk operations against rows that were never fetched.
   *
   * For `mode: 'explicit'` the descriptor's `rows` are sufficient and this
   * method is unnecessary. `descriptor.excludedKeys` and
   * `descriptor.excludedScopes` must be applied by the consumer after resolving
   * the query.
   *
   * @param descriptor - Snapshot from `selectionChange` / `gridActionTriggered`.
   * @returns Where-clause, attribute filters, sort, and per-scope where clauses.
   */
  public resolveSelectionQuery(descriptor: GridSelectionDescriptor): GridSelectionQuery {
    const attributeFilter: Array<GridBackendAttributeFilter> = buildCurrentAttributeFilters(
      this._columnFilters(),
      this.sharedColumnsRef?.() ?? [],
      this.discriminatorColumnsRef?.() ?? {},
      this.remappedFields()
    );
    const sort: Array<GridSortField> | null = this._gridSort();
    const scopeWhere: Array<{ path: string; where: Record<string, unknown> }> = descriptor.scopes.map(
      (scope: { path: string }): { path: string; where: Record<string, unknown> } => ({
        path: scope.path,
        where: this.buildWhereFromPath(scope.path)
      })
    );
    const excludedScopeWhere: Array<{ path: string; where: Record<string, unknown> }> =
      descriptor.excludedScopes.map(
        (scope: { path: string }): { path: string; where: Record<string, unknown> } => ({
          path: scope.path,
          where: this.buildWhereFromPath(scope.path)
        })
      );

    let where: Record<string, unknown> = { ...this.lastGridWhere };
    if (scopeWhere.length === 1) {
      where = scopeWhere[0].where;
    }

    return {
      where,
      attributeFilter,
      sort,
      scopeWhere,
      excludedScopeWhere
    };
  }

  // ── Event Handlers ──────────────────────────────────────────────────

  /**
   * Handles grouping state changes from the grid.
   *
   * Updates group-by signals only; the elements query pipeline already reacts to
   * `_gridGroupByFields` and must not be triggered again here (avoids duplicate fetches).
   *
   * @param state - The grid state containing the updated groupByFields.
   */
  public onGridGroupingChange(state: DataGridState): void {
    const previousPrimary: string | undefined = this._gridGroupByFields()[0];
    this._gridGroupByFields.set(state.groupByFields);
    const newPrimary: string | undefined = state.groupByFields[0];

    if (previousPrimary !== newPrimary) {
      this._primaryGroupField.set(newPrimary ?? null);
      this._groupPagination.set(null);
      this._gridLoading.set(true);
    }
  }

  /**
   * Handles sort changes from the grid component.
   *
   * @param event - The sort change event, or null to clear the sort.
   */
  public onGridSortChange(event: { field: string; direction: SortDirection } | null): void {
    if (event === null) {
      this._gridSort.set(null);
      return;
    }

    const graphqlDirection: GridSortField['direction'] = event.direction === 'asc' ? 'ASC' : 'DESC';
    this._gridSort.set([{ field: event.field, direction: graphqlDirection }]);
  }

  /**
   * Handles filter mode changes from the grid panel.
   *
   * @param mode - The new filter mode.
   */
  public onGridFilterModeChange(mode: GridScopeFilterMode): void {
    this._gridFilterMode.set(mode);
  }

  /**
   * Receives column filter changes from the grid panel, updates the internal
   * signal, and persists the updated filters to the store.
   *
   * @param filters - The updated column filter map.
   */
  public onColumnFiltersChange(filters: ColumnFilterMap): void {
    this._columnFilters.set(filters);
    this.persistence.save({
      ...this.persistence.state(),
      columnFilters: serializeFilters(filters)
    });
  }
}
